/**
 * Agent Hooks using React Query
 * 
 * Handles agent CRUD operations with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAgents, 
  getAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  type Agent,
  type CreateAgentRequest,
  type UpdateAgentRequest,
  type GetAgentsResponse
} from '@/services/agentconfig';
import toast from 'react-hot-toast';
import { useActiveOrgId } from '@/lib/org-scope/react';
import type { OrgId } from '@/lib/org-scope';

/**
 * Query key factory for agents.
 *
 * Every key carries the organization. `getAgents`/`getAgent` send
 * `X-Organization-ID` from the auth store, so the same URL and params return
 * different agents per org — a key without the org id names two different
 * responses, and with a 5-minute `staleTime` Org B was reading Org A's list out
 * of the cache without so much as a refetch.
 */
export const agentKeys = {
  all: ['agents'] as const,
  lists: (orgId: OrgId | null) => [...agentKeys.all, 'list', orgId] as const,
  list: (orgId: OrgId | null, params?: {
    user_id?: string;
    limit?: number;
    offset?: number;
    type?: string;
    status?: string;
  }) => [...agentKeys.lists(orgId), params] as const,
  detail: (orgId: OrgId | null, id: string) => [...agentKeys.all, 'detail', orgId, id] as const,
};

/**
 * Hook to fetch agents list
 * 
 * @example
 * const { data, isLoading, error } = useAgents({
 *   user_id: 'user-123',
 *   limit: 10,
 *   offset: 0,
 *   type: 'code_generator'
 * });
 * // data.agents - array of agents
 * // data.pagination.total_entries - total count for pagination
 * // data.pagination.total_pages - total pages for pagination
 */
export function useAgents(params?: {
  user_id?: string;
  limit?: number;
  offset?: number;
  type?: string;
  status?: string;
}) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: agentKeys.list(orgId, params),
    queryFn: () => getAgents(params),
    enabled: !!params?.user_id, // Only fetch if user_id is provided
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a new agent
 * 
 * @example
 * const createAgent = useCreateAgent();
 * 
 * createAgent.mutate({
 *   name: 'My Agent',
 *   user_id: 'user-123',
 *   type: 'code_generator',
 *   // ...
 * }, {
 *   onSuccess: () => {
 *     toast.success('Agent created!');
 *   }
 * });
 */
export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentData: CreateAgentRequest['agent']) => 
      createAgent(agentData),
    onSuccess: () => {
      // Invalidate all agents queries to refetch
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      toast.success('Agent created successfully!');
    },
  });
}

/**
 * Hook to update an existing agent
 */
export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, agentData }: { agentId: string; agentData: UpdateAgentRequest['agent'] }) => 
      updateAgent(agentId, agentData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      toast.success('Agent updated successfully!');
    },
  });
}

/**
 * Hook to delete an agent
 */
export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => deleteAgent(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      toast.success('Agent deleted successfully!');
    },
  });
}

/**
 * Hook to fetch a single agent by ID
 * 
 * @example
 * const { data: agent, isLoading, error } = useAgent('100');
 */
export function useAgent(agentId: string | null | undefined) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: agentKeys.detail(orgId, agentId || ''),
    queryFn: () => getAgent(agentId!),
    enabled: !!agentId, // Only fetch if agentId is provided
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

